// Small shared UI primitives.
import type { ReactNode } from "react";

export function SampleBadge({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border border-dashed border-coral-deep/60 bg-coral-wash px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide text-coral-deep ${className}`}
      title="Fictional sample story for demonstration. Not a real Databricks announcement."
    >
      Sample · fictional
    </span>
  );
}

export function Pill({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "verified" }) {
  const cls = tone === "verified" ? "bg-mint text-ink" : "bg-sand text-ink";
  return <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>;
}

export function Chevron({ dir }: { dir: "left" | "right" | "up" | "down" }) {
  const d = { left: "m14 6-6 6 6 6", right: "m10 6 6 6-6 6", up: "m6 14 6-6 6 6", down: "m6 10 6 6 6-6" }[dir];
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

export function BookmarkIcon({ filled }: { filled: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-[18px]" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round">
      <path d="M7 4h10a1 1 0 0 1 1 1v15l-6-4-6 4V5a1 1 0 0 1 1-1Z" />
    </svg>
  );
}

export function ExternalIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4" />
    </svg>
  );
}

/** Small type icon (blog / announcement / release note); the label is for screen readers and the tooltip. */
export function TypeIcon({ type, label }: { type: "blog" | "announcement" | "release_note"; label: string }) {
  const d = {
    blog: "M5 5h14M5 9.5h14M5 14h9M5 18.5h6",
    announcement: "M4 10v4h3l6 4V6L7 10H4Zm13-1.5a4 4 0 0 1 0 7",
    release_note: "M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10A.5.5 0 0 1 7 20V3.5Zm7 0v4h4M10 12h5M10 15.5h5",
  }[type];
  return (
    <span title={label} className="inline-flex shrink-0">
      <svg aria-hidden="true" viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/**
 * Three button kinds plus one link style, used everywhere:
 * primary (accent fill, at most one per view), secondary (outlined),
 * ghost/icon (quiet), and link (underlined text).
 */
export const btn = {
  primary:
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-coral px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-coral-press disabled:cursor-not-allowed disabled:opacity-50",
  secondary:
    "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-line-strong bg-card px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-sand disabled:cursor-not-allowed disabled:opacity-50",
  ghost:
    "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-ink-soft transition-colors hover:bg-sand hover:text-ink disabled:cursor-not-allowed disabled:opacity-40",
  icon:
    "inline-grid size-11 place-items-center rounded-lg border border-line-strong bg-card text-ink transition-colors hover:bg-sand disabled:cursor-not-allowed disabled:opacity-35",
  link: "font-semibold text-ink underline decoration-line-strong underline-offset-[3px] hover:decoration-ink",
};

/** Read later + source, as 44 × 44 icon buttons in a card's top-right corner (Browse cards and story pages). */
export function CardActions({ storyId, title, url, saved, onToggleSave, className = "" }: { storyId: string; title: string; url: string; saved: boolean; onToggleSave(): void; className?: string }) {
  return (
    <div className={`flex items-center ${className}`}>
      <button
        type="button"
        onClick={onToggleSave}
        aria-pressed={saved}
        aria-label={saved ? `Saved: remove “${title}” from Reading Basket` : `Read later: save “${title}” to Reading Basket`}
        title={saved ? "Saved" : "Read later"}
        className={`inline-grid size-11 place-items-center rounded-lg transition-colors ${saved ? "text-ink" : "text-ink-soft hover:bg-sand hover:text-ink"}`}
      >
        <BookmarkIcon filled={saved} />
      </button>
      <a href={url} data-source-of={storyId} target="_blank" rel="noopener noreferrer" title="View source" className="inline-grid size-11 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-sand hover:text-ink">
        <ExternalIcon />
        <span className="sr-only">Source for {title} (opens in a new tab)</span>
      </a>
    </div>
  );
}
